import { ddMMyyyyFormat, ddMMyyyyHHmmFormat } from "@app/utils/functions";
import { BaseEntity } from "./BaseEntity";
import { ClientExecutive } from "./ClientExecutive";
import { Country } from "./Country";
import { Course } from "./Course";
import { KLASS_MODALITY, KLASS_TYPE, REQUEST_STATUS } from "./Enums";
import { Instructor } from "./Instructor";
import { instructorNames } from "./Klass";
import { Language } from "./Language";
import { TrainingSpecialistStrategic } from "./TrainingSpecialistStrategic";

/**
 * A request for a class that does not exist yet.
 *
 * Deliberately NOT a Klass, which is what it used to be modelled as, and that is the whole point of
 * the type: a pipeline entry is the ask that comes *before* a class. It has no offering id, it is
 * absent from the Classes list and from `/class/search`, and it lives at its own endpoint
 * (`PipelineKlassApi`, `/pipelineclass`) in its own store.
 *
 * When a request is approved a PrivateKlass or an AdHocKlass is created from it, and the two are
 * tied together by `requestId` - `REQnnnn` for a private class, `ADRnnnn` for an ad hoc one - with
 * `type` recording which of the two the request produced. So given a request, the class it became
 * is `KlassApi.search({ requestId: [...] })`.
 *
 * A request can be turned down, superseded, or left pending forever without ever becoming a class.
 */
export interface PipelineKlass extends BaseEntity {
    /** `REQnnnn` or `ADRnnnn`. Unique, and the key the import upserts on. */
    requestId: string;
    /** When the system that raised the request created it. Sent by that system, not by us. */
    creationDate: Date;
    tss: TrainingSpecialistStrategic;
    city: string;
    country: Country;

    /**
     * The instructors the request is asking for. One plain list, not the requested/allocated/final
     * chain a real class carries: nobody is allocated to a class that does not exist.
     */
    instructors: Instructor[];

    /**
     * Nullable, unlike a class's course. A request arrives carrying a SKU, and one that resolves to
     * nothing has to leave the request importable and visibly incomplete rather than stall the
     * import - so an unset course here means the SKU was not recognised and needs setting by hand.
     */
    course: Course;
    language: Language;
    modality: KLASS_MODALITY;

    /** Which kind of class this request is for, and which kind it becomes once approved. */
    type: KLASS_TYPE;
    customerName: string;
    deliveryDate: Date;
    alternativeDate: Date;
    alternativeDate2: Date;
    requestStatus: REQUEST_STATUS;
    clientExecutive: ClientExecutive;
    remarks: string;
    tssComments: string;
}

/**
 * Flattens a request for export, the same way exportKlassData flattens a class: a Map of
 * <column label, primitive>, keyed by the labels the Pipeline table shows.
 */
export const exportPipelineKlassData = (request: PipelineKlass): Map<string, any> => {
    const _map: Map<string, any> = new Map();

    _map.set("Request Id", request.requestId);
    _map.set("Created", ddMMyyyyHHmmFormat(request.creationDate));
    _map.set("Request Status", request.requestStatus);
    _map.set("Type", request.type);
    _map.set("Customer Name", request.customerName);
    _map.set("Course", request.course?.name);
    _map.set("Course SKU", request.course?.sku);
    _map.set("Modality", request.modality);
    _map.set("Language", request.language?.name);
    _map.set("Delivery Date", ddMMyyyyFormat(request.deliveryDate));
    _map.set("Alternative Date", ddMMyyyyFormat(request.alternativeDate));
    _map.set("Alternative Date 2", ddMMyyyyFormat(request.alternativeDate2));
    _map.set("Instructors", instructorNames(request.instructors));
    _map.set("City", request.city);
    _map.set("Country", request.country?.name);
    _map.set("TSS", request.tss?.name);
    _map.set("Client Executive", request.clientExecutive?.name);
    _map.set("Remarks", request.remarks);
    _map.set("TSS Comments", request.tssComments);

    return _map;
};
